import React from 'react';
import { BasicButton, BasicButtonGroup, InputPageQuestion, InputPageQuestionText, InputPageTitle, InputPageWrapper, StyledOverlayTrigger, 
   StyledTooltip, StyledSelect, StyledSelectItem, StyledTextInput, StyledCheckbox, SubmitButton, Columns,
   StyledParagraph, StyledPlaceInput, SideImage, WarningText, InputRow,
   CheckboxText} from './InputPage.styled';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { populateInputs, allowUpdateElectrify, clearAdvancedConfigs, processAddress } from '../../features/inputs/inputs';
import axios from 'axios';
import { assignSessionId } from '../../features/session/sessionId';
import { assignAnalysisPageNav } from '../../features/analysisPageNav/analysisPageNav';
import Header from '../../components/Header/Header';
import PopUp from '../../components/PopUp/PopUp';
import chargers from '../../assets/chargers.jpg';

function InputPage ({route}) {
   // Dropdown options
   const buildingStatus = ['Existing Building', 'Planned/Under Construction Building'];
   const housingType = ['Affordable Apartments', 'Condominiums', 'Luxury Apartments', 
      'Market Rate Apartments', 'Purpose-Built Student Housing', 'Other']
   const parkingConfig = ['Assigned or Deeded', 'Mixed', 'Unassigned (First Come, First Served)']
   const roles = ['Developer', 'Owner', 'Property Manager', 'Other']

   const states = ['Alabama','Alaska', 'Arizona','Arkansas','California','Colorado',
         'Connecticut','Delaware','District of Columbia', 'Florida','Georgia',
         'Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine',
         'Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
         'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon',
         'Pennsylvania','Puerto Rico','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
         'Virginia','Washington','West Virginia','Wisconsin','Wyoming']
   const housingTypeTooltip = "Your property may not clearly fall into one of these categories. Please select the closest match."
   const parkingConfigTooltip = "Unassigned refers to any parking that is not assigned to individual building residents. \
         Mixed indicates that your building provides access to some assigned and some unassigned or deeded parking spots."
   const roleTooltip = "Please select the role or roles that best describes your relationship to the property. This question will not affect your results."
   const parkingSpotsToElectrifyTooltip = "This field automatically populates with 10% of your parking spaces of this type. This is the percentage we recommend electrifying if you don't have a specific goal."
   const addressTooltip = "Address must include a street number, street name, and city. This information will be used to determine utility and local costs. It will not be shared with third parties or used for marketing purposes."
   const stateTooltip = "The state provided here will be used for calculations, even if it differs from the state in the address field."

   const navigate = useNavigate();
   const inputs = useSelector((state) => state.inputs.value);
   const validInputs = useSelector((state) => state.inputs.valid);
   const token = useSelector((state) => state.token.value);
   const dispatch = useDispatch();
   const sessionId = useSelector((state) => state.sessionId.value);
   const {state} = useLocation();
   const {popup} = state ? state : false;
   const [showPopup, setPopup] = React.useState(popup);
   const [showAddressWarning, setShowAddressWarning] = React.useState(false);

   const renderTooltip = (props) => (
      <StyledTooltip id="button-tooltip" {...props}>
         {props.text}
      </StyledTooltip>
   );

   const handleSelectChange = (e, fieldName) => {
      dispatch(populateInputs({...inputs, [fieldName]: e.target.value}));
      if (fieldName === "state") {
         dispatch(clearAdvancedConfigs());
      }
      if (fieldName === "parkingConfig" && e.target.value !== "Mixed") {
         dispatch(populateInputs({...inputs, [fieldName]: e.target.value, assignedParkingSpaces: null, assignedParkingSpacesToElectrify: null})); 
      }
   }


   const renderSelect = (props, field) => (
      <StyledSelect 
         value = {inputs[field]}
         onChange={(e) => handleSelectChange(e, field)}
      >
         {props.map((prop, index) => (
            <StyledSelectItem value={prop} key={index}>{prop}</StyledSelectItem>
         ))}
      </StyledSelect>
   );

   const renderElecParkingQuestion = () => {
      if (inputs.parkingConfig !== 'Mixed') {
         return (
            <InputPageQuestion row={5} column={1}> 
               <InputPageQuestionText>
                  How many parking spaces are you planning to electrify?
                  <OverlayTrigger style={{display: 'inline-block'}}
                     overlay={renderTooltip({text: parkingSpotsToElectrifyTooltip})}
                  >
                     <StyledOverlayTrigger>i</StyledOverlayTrigger>
                  </OverlayTrigger>
               </InputPageQuestionText>
               <StyledTextInput
                  id="spotsToElectrify"
                  value={inputs.parkingSpacesToElectrify}
                  placeholder="Will autofill with 10% of spaces"
                  onChange={(e) => 
                     {
                        dispatch(allowUpdateElectrify(false));
                        dispatch(populateInputs({...inputs, parkingSpacesToElectrify: Number(e.target.value) !== 0 && !isNaN(Number(e.target.value)) ? Number(e.target.value) : null}, false)); 
                     }
                  }
               />
            </InputPageQuestion>
         );
      } else {
         return (
            <>
            <InputPageQuestion row={5} column={1}> 
               <InputPageQuestionText>
                  How many first come, first served spaces are you planning to electrify?
                  <OverlayTrigger
                     overlay={renderTooltip({text: parkingSpotsToElectrifyTooltip})}
                  >
                     <StyledOverlayTrigger>i</StyledOverlayTrigger>
                  </OverlayTrigger>
               </InputPageQuestionText>
               <StyledTextInput
                  id="spotsToElectrify"
                  value={inputs.parkingSpacesToElectrify}
                  onChange={(e) => 
                     {
                        dispatch(allowUpdateElectrify(false));
                        dispatch(populateInputs({...inputs, parkingSpacesToElectrify: Number(e.target.value) !== 0 && !isNaN(Number(e.target.value)) ? Number(e.target.value) : null}, false)); 
                     }
                  }
               />
            </InputPageQuestion>
            <InputPageQuestion row={5} column={2}> 
               <InputPageQuestionText>
                  How many assigned spaces are you planning to electrify?
                  <OverlayTrigger
                     overlay={renderTooltip({text: parkingSpotsToElectrifyTooltip})}
                  >
                     <StyledOverlayTrigger>i</StyledOverlayTrigger>
                  </OverlayTrigger>
               </InputPageQuestionText>
               <StyledTextInput
                  id="spotsToElectrify"
                  value={inputs.assignedParkingSpacesToElectrify}
                  onChange={(e) => 
                     {
                        dispatch(allowUpdateElectrify(false));
                        dispatch(populateInputs({...inputs, assignedParkingSpacesToElectrify: Number(e.target.value) !== 0 && !isNaN(Number(e.target.value)) ? Number(e.target.value) : null}, false)); 
                     }
                  }
               />
            </InputPageQuestion>
            </>
         );
      }   
   }

   const renderParkingQuestion = () => {
      if (inputs.parkingConfig !== 'Mixed') {
         return (
            <InputPageQuestion row={4} column={2}> 
               <InputPageQuestionText>
                  How many parking spaces are at your building?
               </InputPageQuestionText>
               <StyledTextInput
               id="assignedParking"
               value={inputs.parkingSpaces}
               onChange={(e) => 
                  {
                     dispatch(populateInputs({...inputs, parkingSpaces: Number(e.target.value) !== 0 && !isNaN(Number(e.target.value)) ? Number(e.target.value) : null}));
                  }
               }
               />
            </InputPageQuestion>
         );
      } else {
         return (
            <>
               <InputPageQuestion row={4} column={2}> 
                  <InputPageQuestionText>
                     How many parking spaces are first come, first served at your building?
                  </InputPageQuestionText>
                  <StyledTextInput
                  id="assignedParking"
                  value={inputs.parkingSpaces}
                  onChange={(e) => 
                     {
                        dispatch(populateInputs({...inputs, parkingSpaces: Number(e.target.value) !== 0 && !isNaN(Number(e.target.value)) ? Number(e.target.value) : null}));
                     }
                  }
                  />
            </InputPageQuestion>
            <InputPageQuestion row={4} column={3}> 
                  <InputPageQuestionText>
                     How many parking spaces are assigned or deeded at your building?
                  </InputPageQuestionText>
                  <StyledTextInput
                  id="assignedParking"
                  value={inputs.assignedParkingSpaces}
                  onChange={(e) => 
                     {
                        dispatch(populateInputs({...inputs, assignedParkingSpaces: Number(e.target.value) !== 0 && !isNaN(Number(e.target.value)) ? Number(e.target.value) : null}));
                     }}
                  />
            </InputPageQuestion>
         </>
         );
      }
   }

   const finalUpdates = () => {
      const role = inputs.customRole !== null ? [...inputs.role.filter(r => roles.includes(r)), inputs.customRole] : inputs.role.filter(r => roles.includes(r));
      if (inputs.parkingSpacesToElectrify === null ) {
         return {...inputs, parkingSpacesToElectrify: Math.ceil((inputs.parkingSpaces)/10), role: role};
      }
      if (inputs.assignedParkingSpacesToElectrify === null && inputs.assignedParkingSpaces) {
         return {...inputs, assignedParkingSpacesToElectrify: Math.ceil((inputs.assignedParkingSpaces)/10), role: role};
      }
      return {...inputs, role: role};
   };

   const submit = () => {
      dispatch(clearAdvancedConfigs());
      dispatch(populateInputs({...inputs}));
      dispatch(allowUpdateElectrify(false));
      if (validInputs) {
         axios.post(`${process.env.REACT_APP_API_URL}api/inputspush`,
            {...finalUpdates(inputs), freshRun: true},
            { headers: 
               { 
               'Authorization': `${token}`, 
               'Content-Type': 'application/json',
               'sessionId': `${sessionId}`
               }
            }
         )
         .then(response => {
            dispatch(assignSessionId(response.data['sessionId']));
            dispatch(assignAnalysisPageNav('costs'));
            navigate('/loading');
         })
         .catch(error => {
            console.error(error);
         });
      }
   }

   return (
   <>
   <PopUp show={showPopup} handleClose={() => setPopup(false)}/>
   <Header></Header>
   <Columns>
   <SideImage src={chargers} alt="Image of EV Chargers" />
   <InputPageWrapper>
      <InputPageTitle>Tell us about your project
      <StyledParagraph><b>Please note all fields are required.</b></StyledParagraph>
      </InputPageTitle>
      <InputPageQuestion row={2} column={1} span={2}> 
         <InputPageQuestionText>
            What is the status of this property?
         </InputPageQuestionText>
         <BasicButtonGroup width={'80%'}>
            {buildingStatus.map((status, index) => (
               <BasicButton 
                     key={index} 
                     numButtons = {status.length}
                     width = {'15vw'}
                     selected = {(!inputs.newDevelopment && status === 'Existing Building') || (inputs.newDevelopment && status === 'Planned/Under Construction Building')}
                     onClick={() => dispatch(populateInputs({...inputs, newDevelopment: status === 'Planned/Under Construction Building'}))}
               >
                  {status}
               </BasicButton>
            ))}
         </BasicButtonGroup>
      </InputPageQuestion>
         <InputPageQuestion row={3} column={1}> 
            <InputPageQuestionText>
               What type of housing is it?
               <OverlayTrigger
                  overlay={renderTooltip({text: housingTypeTooltip})}
               >
                  <StyledOverlayTrigger>i</StyledOverlayTrigger>
               </OverlayTrigger>
            </InputPageQuestionText>
            {renderSelect(housingType, "propertyType")}
         </InputPageQuestion>
         <InputPageQuestion row={3} column={2}> 
            <InputPageQuestionText>
               What is the parking configuration?
               <OverlayTrigger
                  overlay={renderTooltip({text: parkingConfigTooltip})}
               >
                  <StyledOverlayTrigger>i</StyledOverlayTrigger>
               </OverlayTrigger>
            </InputPageQuestionText>
            {renderSelect(parkingConfig, "parkingConfig")}
         </InputPageQuestion>
         <InputPageQuestion row={3} column={3}> 
            <InputPageQuestionText>
               What year was the building constructed?
            </InputPageQuestionText>
            <StyledTextInput
            type="year"
            id="yearBuilt"
            value={inputs.yearBuilt}
            onChange={(e) => dispatch(populateInputs({...inputs, yearBuilt: Number(e.target.value) !== 0 && !isNaN(Number(e.target.value)) ? Number(e.target.value) : null}))}
            required
            />
         </InputPageQuestion>

      <InputPageQuestion row={4} column={1}> 
            <InputPageQuestionText>
               How many units are in the building?
            </InputPageQuestionText>
            <StyledTextInput
            id="numUnits"
            value={inputs.units}
            onChange={(e) => dispatch(populateInputs({...inputs, units: Number(e.target.value) !== 0 && !isNaN(Number(e.target.value)) ? Number(e.target.value) : null}))}
            />
         </InputPageQuestion>
         {renderParkingQuestion()}
         {renderElecParkingQuestion()}
         <InputPageQuestion row={inputs.parkingConfig === "Mixed" ? 6: 5} column={inputs.parkingConfig === "Mixed" ? 1: 2}> 
            <InputPageQuestionText>
               Where is the property?
               <OverlayTrigger
                  overlay={renderTooltip({text: addressTooltip})}
               >
                  <StyledOverlayTrigger>i</StyledOverlayTrigger>
               </OverlayTrigger>
            </InputPageQuestionText>
            {showAddressWarning && !(inputs.streetAddress && inputs.city && inputs.zip) ? <WarningText> Address is missing a street number. Please select a complete address.</WarningText> : null}
            <StyledPlaceInput
            apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
            options={{
               types: ['address'],
               componentRestrictions: {country: 'us'},
               fields: ['address_components', 'formatted_address']
            }}
            defaultValue={inputs.streetAddress}
            onChange={(e) => {dispatch(populateInputs({...inputs, streetAddress: null})); setShowAddressWarning(true)}}
            onPlaceSelected={(place) => {
               dispatch(processAddress({address_components: place.address_components, formatted_address: place.formatted_address}));
            }}
            />
         </InputPageQuestion>
         <InputPageQuestion row={inputs.parkingConfig === "Mixed" ? 6: 5} column={inputs.parkingConfig === "Mixed" ? 2: 3}> 
            <InputPageQuestionText>
               Which state is the property in?
               <OverlayTrigger
                     overlay={renderTooltip({text: stateTooltip})}
               >
                  <StyledOverlayTrigger>i</StyledOverlayTrigger>
               </OverlayTrigger>
            </InputPageQuestionText>
            {renderSelect(states, "state")}
         </InputPageQuestion>
         <InputPageQuestion row={inputs.parkingConfig === "Mixed" ? 7: 6} column={1} span={3}> 
         <InputPageQuestionText>
            What is your role?
            <OverlayTrigger
               overlay={renderTooltip({text: roleTooltip})}
            >
               <StyledOverlayTrigger>i</StyledOverlayTrigger>
            </OverlayTrigger>
         </InputPageQuestionText>
         <BasicButtonGroup width={'100%'} question={'role'}>
            {roles.map((role, index) => (
               <BasicButton 
                     key={index} 
                     numButtons = {roles.length}
                     selected = {inputs.role.includes(role)}
                     width = {'15%'}
                     onClick={() => {
                        var newRoleSet = [...inputs.role]; // Copy the current set
                        if (newRoleSet.includes(role)) {
                           newRoleSet = newRoleSet.filter(r => r !== role); // Remove the role if already present
                           if (role === 'Other') {
                              newRoleSet = newRoleSet.filter(r => roles.includes(r)); // Remove all other roles if 'Other' is selected
                           }
                        } else {
                           newRoleSet = [...newRoleSet, role]; // Add the role if not already present
                        }
                        dispatch(populateInputs({ ...inputs, role: newRoleSet })); // Dispatch the updated set
                        
                    }}               >
                  {role}
               </BasicButton>
            ))}
            <StyledTextInput
               question={"role1"}
               id="role"
               width={'30%'}
               height={'46px'}
               value={inputs.customRole}
               placeholder="If other, please specify"
               onChange={(e) => dispatch(populateInputs({...inputs, customRole: e.target.value}))}
               disabled = {!inputs.role.includes('Other')}
               required
            />
         </BasicButtonGroup>
      </InputPageQuestion>
      <StyledTextInput
               question={"role2"}
               id="role"
               height={'46px'}
               width={'90%'}
               value={inputs.customRole}
               placeholder="If other, please specify"
               onChange={(e) => dispatch(populateInputs({...inputs, customRole: e.target.value}))}
               disabled = {!inputs.role.includes('Other')}
               required
      />
      <InputPageQuestion row={inputs.parkingConfig === "Mixed" ? 8: 7} column={1} span={3}>
      <InputRow>
      <StyledCheckbox
         type="checkbox"
         id="optIn"
         checked={inputs.optIn}
         onChange={(e) => dispatch(populateInputs({...inputs, optIn: e.target.checked, emailAddress: null}))}/>
         <CheckboxText>Please contact me about about multifamily housing EV charging grants or funding opportunities.</CheckboxText>
         </InputRow>
         <StyledTextInput
               question={"emailAddress"}
               id="emailAddress"
               height={'46px'}
               width={'90%'}
               value={inputs.optIn ? inputs.emailAddress : ''}
               placeholder={"Email Address"}
               onChange={(e) => dispatch(populateInputs({...inputs, emailAddress: e.target.value}))}
               display={inputs.optIn ? 'flex' : 'none'}
         />
      </InputPageQuestion>
      <SubmitButton 
         onClick={() => submit()}
         row={inputs.parkingConfig === "Mixed" ? 9: 8}
         column={1}
         disabled={!validInputs}   
      > Submit </SubmitButton>
   </InputPageWrapper> 
   </Columns>
   </>
   );
};

InputPage.propTypes = {
};

export default InputPage;
